/* ============================================================================
 * ShipperCompanyProfile — read-only view of the shipper's own supplier
 * record. The shipper sees what GFI has on file (legal name, RFC, address,
 * contacts) but cannot edit it; changes go through GFI staff via
 * SupplierRegistry.
 * ============================================================================ */

const T = {
  en: {
    title: "Company Profile",
    sub: "This is the data Ground Fresh International has on file for your company. Contact GFI to request changes.",
    section_identity: "Identity",
    section_fiscal: "Fiscal",
    section_contact: "Contact",
    section_address: "Address",
    section_contacts: "Contacts on file",
    contactName: "Name",
    initials: "Initials", shortName: "Short name", fullName: "Legal name",
    country: "Country", rfc: "RFC", taxId: "Tax ID", fdaNumber: "FDA #",
    phone: "Phone", email: "Email", website: "Website",
    address: "Address", colony: "Colony", city: "City", state: "State", zip: "Zip",
    noContacts: "No contacts on file.",
    none: "—",
  },
  es: {
    title: "Perfil de Empresa",
    sub: "Estos son los datos que Ground Fresh International tiene de tu empresa. Para solicitar cambios contacta a GFI.",
    section_identity: "Identificación",
    section_fiscal: "Fiscal",
    section_contact: "Contacto",
    section_address: "Dirección",
    section_contacts: "Contactos registrados",
    contactName: "Nombre",
    initials: "Iniciales", shortName: "Nombre corto", fullName: "Razón social",
    country: "País", rfc: "RFC", taxId: "Tax ID", fdaNumber: "FDA #",
    phone: "Teléfono", email: "Correo", website: "Sitio web",
    address: "Dirección", colony: "Colonia", city: "Ciudad", state: "Estado", zip: "C.P.",
    noContacts: "Sin contactos registrados.",
    none: "—",
  },
};

function Field({ label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text)" }}>{value || "—"}</span>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: "16px 20px", marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

export default function ShipperCompanyProfile({ supplier, lang }) {
  const t = T[lang === "en" ? "en" : "es"];
  const s = supplier || {};

  const contactName = function (c) {
    if (!c) return "";
    if (c.firstName || c.lastName) return [c.firstName, c.middleName, c.lastName].filter(Boolean).join(" ");
    return [c.nombre, c.apPaterno, c.apMaterno].filter(Boolean).join(" ");
  };
  const phoneStr = function (c) {
    if (!c) return "";
    return [c.phoneCountry ? "+" + c.phoneCountry : "", c.phoneArea, c.phoneNumber].filter(Boolean).join(" ");
  };

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{t.title}</h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-muted)" }}>{t.sub}</p>
      </div>

      <Section title={t.section_identity}>
        <div style={{ display: "grid", gridTemplateColumns: "100px 180px 1fr", gap: 16 }}>
          <Field label={t.initials} value={s.initials} />
          <Field label={t.shortName} value={s.shortName} />
          <Field label={t.fullName} value={s.fullName} />
        </div>
      </Section>

      <Section title={t.section_fiscal}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <Field label={t.country} value={s.country} />
          <Field label={t.rfc} value={s.rfc} />
          <Field label={t.taxId} value={s.taxId} />
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label={t.fdaNumber} value={s.fdaNumber} />
        </div>
      </Section>

      <Section title={t.section_contact}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <Field label={t.phone} value={[s.phoneCountry ? "+" + s.phoneCountry : "", s.phoneArea, s.phoneNumber].filter(Boolean).join(" ")} />
          <Field label={t.email} value={s.email} />
          <Field label={t.website} value={s.website} />
        </div>
      </Section>

      <Section title={t.section_address}>
        <Field label={t.address} value={s.address} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginTop: 12 }}>
          <Field label={t.colony} value={s.colony} />
          <Field label={t.city} value={s.city} />
          <Field label={t.state} value={s.state} />
          <Field label={t.zip} value={s.zip} />
        </div>
      </Section>

      <Section title={t.section_contacts}>
        {(!s.contacts || s.contacts.length === 0) ? (
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.noContacts}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {s.contacts.map(function (c, i) {
              return (
                <div key={c.id || i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "10px 12px", background: "var(--color-bg)", borderRadius: 6, border: "1px solid var(--color-border)" }}>
                  <Field label={t.contactName} value={contactName(c)} />
                  <Field label={t.phone} value={phoneStr(c)} />
                  <Field label={t.email} value={c.email} />
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}
